import {
  CalendarDate,
  DateValue,
  parseAbsolute,
  toCalendarDateTime,
} from '@internationalized/date';
import { Spacing } from '@persona-id/design-tokens';
import { CircleXInverseIcon } from '@persona-id/design-tokens/icons';
import { DateRangePickerProps as DateRangePickerBase } from '@react-types/datepicker';
import { RangeValue } from '@react-types/shared';
import { Settings } from 'luxon';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import {
  mergeProps,
  useButton,
  useDateRangePicker,
  useFocusWithin,
  usePress,
  VisuallyHidden,
} from 'react-aria';
import OutsideClickHandler from 'react-outside-click-handler';
import { useDateRangePickerState } from 'react-stately';
import styled from 'styled-components';

import { AnalyticsDateRange } from 'dashboard/components/AnalyticsDatePicker/AnalyticsDatePicker';
import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import RangeCalendar from 'design-system/components/DatePicker/Calendar/RangeCalendar';
import DateField from 'design-system/components/DatePicker/DateField';
import { ClearButton } from 'design-system/components/Input';
import Popover from 'design-system/components/Popover';
import { isDisabled } from 'design-system/lib/pseudoClass';

const DateRangeContainer = styled.div`
  display: inline-flex;
  flex-direction: column;
  width: 100%;
`;

const DateRangeInputs = styled.div`
  display: flex;
  align-items: center;
`;

export interface DateRangePickerProps extends DateRangePickerBase<DateValue> {
  dateRange: AnalyticsDateRange;
  onChange: (range: RangeValue<DateValue> | null) => void;
  disabled?: boolean;
  minDate?: DateValue;
  maxDate?: DateValue;
  label?: string | null;
  helpText?: string;
  dataTest?: string;
  id?: string;
  hidePresets?: boolean;
  className?: string;
  /**
   * Whether or not to display clear button
   */
  showClear?: boolean;
  /**
   * Called when the clear button is clicked/pressed
   */
  onClear?: () => void;
  /**
   * z-index of the calendar popover
   */
  calendarZIndex?: number;
}

const OpenButton = ({ calendarRef }: { calendarRef: MutableRefObject<HTMLElement> }) => {
  const openCalBtnRef = useRef();
  const { buttonProps } = useButton(
    {
      onPress: () => {
        if (calendarRef.current) {
          (
            calendarRef.current.querySelector('button[aria-label="Previous"]') as HTMLButtonElement
          ).focus();
        }
      },
    },
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    openCalBtnRef,
  );

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return <button {...buttonProps} ref={openCalBtnRef} aria-label='Open calendar' />;
};

const ClearRangeButton = styled(ClearButton)`
  height: 16px;
  width: 16px;
  margin-left: ${Spacing[2]};

  &${isDisabled}, &:disabled {
    background-color: transparent;
  }
`;

const DateRangePicker = ({
  granularity = 'day',
  hideTimeZone = true,
  className,
  calendarZIndex,
  ...props
}: DateRangePickerProps) => {
  const state = useDateRangePickerState({ granularity, hideTimeZone, ...props });
  const ref = useRef(null);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [dateMode, setDateMode] = useState<'start' | 'end'>(null);
  const { groupProps, startFieldProps, endFieldProps, calendarProps, labelProps } =
    useDateRangePicker({ granularity, hideTimeZone, ...props }, state, ref);

  useEffect(() => {
    // If not set, return early
    if (!props.dateRange.startDate || !props.dateRange.endDate) {
      return;
    }

    // Find out the real time by adjusting the date to the user's timezone
    const startDate = props.dateRange.startDate.setZone(Settings.defaultZone);
    const endDate = props.dateRange.endDate.setZone(Settings.defaultZone);
    // Months are 1-indexed, no translations need to happen to CalendarDates
    const startCalendar = state.hasTime
      ? /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        parseAbsolute(startDate.toISO(), Settings.defaultZone.name)
      : new CalendarDate(startDate.year, startDate.month, startDate.day);
    const endCalendar = state.hasTime
      ? /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        parseAbsolute(endDate.toISO(), Settings.defaultZone.name)
      : new CalendarDate(endDate.year, endDate.month, endDate.day);

    const dateRangeV2: RangeValue<DateValue> = {
      start: startCalendar,
      end: endCalendar,
    };

    state.setValue(dateRangeV2);
  }, [props.dateRange.startDate, props.dateRange.endDate, granularity]);

  const calendarRef = useRef(null);
  const { focusWithinProps: startFocusWithinProps } = useFocusWithin({
    onFocusWithin: () => {
      setDateMode('start');
      if (!state.isOpen) {
        state.setOpen(true);
      }
    },
  });

  const { focusWithinProps: endFocusWithinProps } = useFocusWithin({
    onFocusWithin: () => {
      setDateMode('end');
      if (!state.isOpen) {
        state.setOpen(true);
      }
    },
  });

  const handleOutsideClick = (e: MouseEvent) => {
    // When there's a click outside of the containers + calendars, reset the highlighted date
    // Only close the calendar if it's open and we've clicked something outside
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    if (!calendarRef?.current?.contains(e.target) && state.isOpen) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setDateMode(null);
      state.setOpen(false);
    }
  };

  const { pressProps: pressClearProps } = usePress({
    onPress: () => {
      state.setValue(null);

      if (props.onClear) {
        props.onClear();
      }
    },
  });

  const isValueNull = state.value && state.value.start == null && state.value.end == null;

  return (
    <OutsideClickHandler onOutsideClick={handleOutsideClick}>
      <DateRangeContainer data-test={props.dataTest} className={className}>
        {props.label && <FormControlLabel {...labelProps}>{props.label}</FormControlLabel>}
        {props.helpText && <HelpIcon>{props.helpText}</HelpIcon>}
        <Popover
          open={state.isOpen}
          contentProps={{
            align: 'start',
            onEscapeKeyDown: () => {
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              setDateMode(null);
              state.setOpen(false);
            },
            noPadding: true,
            style: { zIndex: calendarZIndex },
          }}
        >
          <Popover.Trigger asChild>
            <DateRangeInputs {...mergeProps(groupProps)} ref={ref}>
              {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
              <DateField
                inputPosition='start'
                focused={dateMode === 'start'}
                disabled={props.disabled}
                {...mergeProps(startFieldProps, startFocusWithinProps)}
              />
              {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
              <DateField
                inputPosition='end'
                focused={dateMode === 'end'}
                disabled={props.disabled}
                {...mergeProps(endFieldProps, endFocusWithinProps)}
              />
              {props.showClear && (
                <ClearRangeButton
                  {...pressClearProps}
                  disabled={isValueNull}
                  aria-label='Clear date range'
                >
                  <CircleXInverseIcon />
                </ClearRangeButton>
              )}
              <VisuallyHidden>
                {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
                <OpenButton calendarRef={calendarRef} />
              </VisuallyHidden>
            </DateRangeInputs>
          </Popover.Trigger>
          <RangeCalendar
            {...{ ...calendarProps, autoFocus: null }}
            calendarRef={calendarRef}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            setDateMode={setDateMode}
            setValue={state.setValue}
            granularity={granularity}
            hidePresets={props.hidePresets}
            onSelectDate={(dateVal) => {
              /**
               * Please use caution when editing the logic below.
               * It is specific to react-aria's date picker and can be brittle.
               */
              if (dateVal) {
                const date = state.hasTime ? toCalendarDateTime(dateVal) : dateVal;

                if (dateMode === 'start') {
                  // Handle a case where we've deleted both dates
                  if (state.value.end == null && state.value.start == null) {
                    state.setValue({
                      start: date,
                      end: date,
                    });
                  }
                  // If end becomes the start
                  else if (state.value.end && date.compare(state.value.end) > 0) {
                    state.setValue({
                      start: state.value.end,
                      end: state.value.end.set(dateVal), // Preserve time
                    });
                  } else {
                    state.setValue({
                      // if start is not set, set it to the date or date + time value, otherwise set the dateVal to preserve time
                      start: state.value.start == null ? date : state.value.start.set(dateVal),
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */

                      end: state.value.end,
                    });
                  }
                  setDateMode('end');
                } else {
                  // Handle a case where we've deleted both dates
                  if (state.value.end == null && state.value.start == null) {
                    state.setValue({
                      start: date,
                      end: date,
                    });
                  }
                  // If start becomes the end
                  else if (state.value.start && date.compare(state.value.start) < 0) {
                    state.setValue({
                      start: state.value.start.set(dateVal),
                      end: state.value.start,
                    });
                  } else {
                    state.setValue({
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      start: state.value.start,
                      // if end is not set, set it to the date or date + time value, otherwise set the dateVal to preserve time
                      end: state.value.end == null ? date : state.value.end.set(dateVal),
                    });
                  }
                  setDateMode('start');
                }
              }
            }}
            minDate={props.minDate}
            maxDate={props.maxDate}
          />
        </Popover>
      </DateRangeContainer>
    </OutsideClickHandler>
  );
};

export default DateRangePicker;
